<template>
  <div v-loading="loading" class="product-container">
    <!-- 头部搜索区域 -->
    <div class="search-header">
      <div class="search-box">
        <el-input 
            v-model="searchKeyword" 
            placeholder="请输入产品名称搜索" 
            class="search-input"
            clearable
            @keyup.enter="handleSearch"/>
        <el-button type="primary" @click="handleSearch">搜索</el-button>
      </div>
    </div>

    <!-- 正文区域：左侧树结构 + 右侧产品列表 -->
    <div class="content-wrapper">
      <!-- 左侧树结构 -->
      <div class="tree-sidebar">
        <div class="tree-title">产品分类</div>
        <el-tree 
            ref="categoryTree"
            :data="categoryTreeData" 
            :props="treeProps"
            :current-node-key="null"
            :expand-on-click-node="false"
            node-key="materialID"
            default-expand-all
            @node-click="handleTreeClick"
        />
      </div>

      <!-- 右侧产品列表 -->
      <div class="product-content">
        <div class="content-header">
          <h2>产品列表</h2>
          <span class="product-count">共 {{products.length}} 件产品</span>
        </div>

        <div class="product-list">
          <div v-for="product in products" :key="product.materialID" class="product-item">
            <div class="product-image">
              <el-image
                  v-if="product.imgUrl"
                  :src="product.imgUrl"
                  alt="产品封面图"
                  style="width: 180px; height: 180px;"
                  :preview-src-list="[product.imgUrl]"/>
              <i v-else class="el-icon-picture-outline" style="font-size: 80px; color: #ddd;"/>
            </div>
            <div class="product-info">
              <h3 class="product-name" :title="product.materialName">{{product.materialName}}</h3>
              <div class="product-category">分类: {{product.parentName}}</div>
              <el-button type="primary" :loading="product.loading" @click="addToCart(product)">加入购物车</el-button>
            </div>
          </div>
        </div>

        <!-- 空状态 -->
        <div v-if="products.length === 0" class="empty-state">
          <i class="el-icon-search" style="font-size: 48px; color: #ccc;"/>
          <p>暂无产品数据</p>
        </div>
      </div>
    </div>

    <product-add-to-cart ref="productAddToCart"/>
  </div>
</template>

<script>
  import ProductAddToCart from "./ProductAddToCart.vue";
  
  export default {
    name: "ProductList",
    components: { ProductAddToCart },
    data() {
      return {
        products: [],
        loading: false,
        searchKeyword: "",
        selectedCategoryId: null,
        // 树结构数据（示例数据，实际应从接口获取）
        categoryTreeData: [],
        treeProps: {
          label: "materialName",
          pid: "parentID", 
          id: "materialID",
          children: "",
        },
      };
    },
    mounted() {
      this.getData();
      // 实际项目中应该先获取分类树数据
      this.getCategoryTree();
    },
    methods: {
      // 获取分类树数据
      getCategoryTree() {
        // 实际应调用接口获取分类树
        this.$http.get("basic", "materials/getAllList?sellType=20").then(res => {
          this.categoryTreeData = [
            {
              materialID: null,
              materialName: "全部",
            },
            ...res,
          ];
        });
      },
      
      // 获取产品列表
      getData(categoryId = null, keyword = "") {
        this.loading = true;
        const params = { 
          sellType: 20, 
        };
        if (categoryId !== null) {
          params.materialID = categoryId;
        }
        if (keyword) {
          params.keyword = keyword;
        }
        this.$http.get("basic", "materials/getPageList", params).then(res => {
          this.products = res.records.map(item => ({
            ...item,
            imgUrl: item.images[0] ? item.images[0].filePath : null,
            loading: false,
          }));
        }).finally(() => {
          this.loading = false;
        });
      },
      // 搜索
      handleSearch() {
        this.selectedCategoryId = null;
        this.getData(null, this.searchKeyword);
      },
      // 点击树节点
      handleTreeClick(data, node) {
        this.selectedCategoryId = data.materialID;
        this.getData(data.materialID, this.searchKeyword);
      },
      // 获取分类名称
      getCategoryName(categoryId) {
        // 实际应根据categoryId获取分类名称
        return "未分类";
      },
      // 打开产品属性弹窗
      addToCart(product) {
        product.loading = true;
        this.$refs.productAddToCart.openByProductId(product).finally(() => {
          product.loading = false;
        });
      },
    },
  };
</script>

<style lang="scss" scoped>
::v-deep {
  .el-tree-node.is-current{
    background-color: #f5f7fa;
  }
}
.product-container {
  height: calc(100vh - 73px);
  display: flex;
  flex-direction: column;
  background-color: #f5f7fa;

  /* 搜索头部 */
  .search-header {
    padding: 16px 20px;
    background-color: #fff;
    border-bottom: 1px solid #e4e7ed;

    .search-box {
      display: flex;
      gap: 12px;
      max-width: 500px;

      .search-input {
        flex: 1;
      }
    }
  }

  /* 内容区域 */
  .content-wrapper {
    flex: 1;
    display: flex;
    overflow: hidden;
  }

  /* 左侧树结构 */
  .tree-sidebar {
    width: 260px;
    background-color: #fff;
    border-right: 1px solid #e4e7ed;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .tree-title {
      padding: 16px 20px;
      font-size: 14px;
      font-weight: 600;
      color: #303133;
      border-bottom: 1px solid #e4e7ed;
    }

    :deep(.el-tree) {
      flex: 1;
      overflow-y: auto;
      padding: 10px;
    }
  }

  /* 右侧产品列表 */
  .product-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: 20px;

    .content-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;

      h2 {
        font-size: 20px;
        color: #303133;
        margin: 0;
      }

      .product-count {
        font-size: 14px;
        color: #606266;
      }
    }

    .product-list {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
      gap: 20px;
      overflow-y: auto;
      padding-right: 10px;

      .product-item {
        border: 1px solid #e4e7ed;
        border-radius: 4px;
        padding: 15px;
        background-color: #fff;
        transition: all 0.3s;

        &:hover {
          box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
        }

        .product-image {
          height: 200px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 15px;
          background-color: #f5f7fa;
          border: 1px dashed #d9d9d9;
          border-radius: 4px;
          flex-direction: column;
          img {
            max-width: 100%;
            max-height: 100%;
            object-fit: cover;
          }
        }

        .product-info {
          text-align: center;
          position: relative;

          .product-name {
            font-size: 14px;
            margin-bottom: 8px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            white-space: normal;
            position: relative;
            cursor: default;
            color: #303133;
          }

          .product-category {
            font-size: 12px;
            color: #909399;
            margin-bottom: 12px;
          }

          .el-button {
            width: 120px;
          }
        }
      }
    }

    /* 空状态 */
    .empty-state {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: #909399;

      p {
        margin-top: 16px;
        font-size: 14px;
      }
    }
  }
}
</style>